import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp';
import { Draggable } from 'react-beautiful-dnd';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'clsx';

import { useContextTypes } from '../../../hooks/use-context-types';
import { DisplayGridColumnPropType } from '../../../prop-types/display-grid-column';

import { StringHelperService } from '../../../services/string-helper-service';

import { setIsResizing } from '../actions';
import { Context } from '../context';
import { DisplayGridCell } from '../display-grid-cell/index';

import style from './cell.module.css';
import rowStyle from './index.module.less';

export const DisplayGridHeaderCell = ({
  column,
  elementId,
  index,
  isDraggable = false,
  onColumnResize,
  onColumnSortChange,
  onResizeComplete,
  isActiveGroupBy = false,
}) => {
  useContextTypes('DisplayGridHeaderCell', Context, {
    dispatch: PropTypes.func.isRequired,
    getColumnId: PropTypes.func.isRequired,
  });

  const dragover = useRef();
  const rAF = useRef();
  const ref = useRef();
  const resizeClientX = useRef(null);
  const wasResizing = useRef(false);
  const { dispatch, getColumnId } = useContext(Context);

  useEffect(() => {
    return () => {
      if (rAF.current) {
        window.cancelAnimationFrame(rAF.current);
      }

      if (dragover.current) {
        global.document.removeEventListener('dragover', dragover.current);
      }
    };
  }, []);

  const onClick = useCallback(
    (event) => {
      if (column.nonsortable || wasResizing.current) {
        event.preventDefault();
        wasResizing.current = false;
        return;
      }

      onColumnSortChange(column);
    },
    [column, onColumnSortChange],
  );

  /**
   * This is called on each drag operation, to resize the columns in state,
   * so that we can offer a realtime view of resizing entire columns. We
   * update the state here on each incoming drag event, and only onDragEnd
   * do we broadcast that outwardly to any consumers in the form of new
   * columns. We can call setIsResizing constantly, because it won't trigger
   * a state update at the reducer unless the value is actually different.
   */
  const onDrag = useCallback(() => {
    if (rAF.current) {
      return;
    }

    rAF.current = global.window.requestAnimationFrame(() => {
      rAF.current = undefined;

      if (resizeClientX.current) {
        const { minWidth } = column;

        const dimensions = ref.current.getBoundingClientRect();
        const newWidth = Math.max(minWidth, resizeClientX.current - dimensions.left);

        if (column.width !== newWidth) {
          onColumnResize({
            ...column,
            width: newWidth,
          });
        }

        resizeClientX.current = null;
      }
    });
  }, [column, onColumnResize]);

  const onDragEnd = useCallback(() => {
    onResizeComplete();

    wasResizing.current = true;

    global.document.removeEventListener('dragover', dragover.current);
    dragover.current = undefined;
  }, [onResizeComplete]);

  const onDragStart = useCallback((event) => {
    event.dataTransfer.setData('thisLineIsNeededToMakeFirefoxWork', 'oy vey');

    dragover.current = (e) => {
      resizeClientX.current = e.clientX;
    };

    global.document.addEventListener('dragover', dragover.current);

    dispatch(setIsResizing(true));
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderedResizeGripper = useMemo(
    () => <div className={style['header-cell-resize-gripper']} draggable onDrag={onDrag} onDragEnd={onDragEnd} onDragStart={onDragStart} />,
    [onDrag, onDragEnd, onDragStart],
  );

  const flexStyles = useMemo(
    () => ({
      flexBasis: column.width || column.minWidth,
      width: column.width || column.minWidth,
    }),
    [column],
  );

  const icon = useMemo(() => {
    return column.sorted === 'ascending' ? <FontAwesomeIcon icon={faCaretUp} /> : <FontAwesomeIcon icon={faCaretDown} />;
  }, [column]);

  const getDraggableCellStyles = useCallback(
    (isDragging) => {
      if (!isDragging) {
        return { width: column.width || column.minWidth };
      }
    },
    [column],
  );

  const renderedIcon = useMemo(() => {
    if (column.nonsortable) {
      return null;
    }

    return <div className={style['header-icon']}>{icon}</div>;
  }, [column, icon]);

  const memoizedColumn = useMemo(() => ({ id: column.id }), [column.id]);

  const renderDraggableCell = () => {
    const { name, sorted } = column;
    const cellId = `${elementId}-header-cell-${StringHelperService.idify(getColumnId(column))}`;

    return (
      <Draggable key={cellId} draggableId={cellId} index={index}>
        {(provided, snapshot) => {
          const className = classnames({
            [rowStyle.sorted]: sorted,
            [style.dragging]: snapshot.isDragging,
            [style['non-sortable']]: column.nonsortable,
            [rowStyle['is-active-groupby']]: isActiveGroupBy,
          });
          const draggableCellStyles = getDraggableCellStyles(snapshot.isDragging);

          return (
            <div className='draggable-header-cell' style={draggableCellStyles}>
              {renderedResizeGripper}
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                style={{ ...flexStyles, ...provided.draggableProps.style }}
                {...provided.dragHandleProps}
              >
                <DisplayGridCell ref={ref} className={className} column={memoizedColumn} onClick={onClick}>
                  <div className={style['header-name']}>{name}</div>
                  {renderedIcon}
                </DisplayGridCell>
              </div>
            </div>
          );
        }}
      </Draggable>
    );
  };

  const renderNonDraggableCell = () => {
    const { name, sorted } = column;
    const className = classnames({
      [rowStyle.sorted]: sorted,
      [style['non-sortable']]: column.nonsortable,
    });

    return (
      <DisplayGridCell ref={ref} className={className} column={memoizedColumn} onClick={onClick} style={flexStyles}>
        <div className={style['header-name']}>{name}</div>
        {renderedIcon}
        {renderedResizeGripper}
      </DisplayGridCell>
    );
  };

  return isDraggable ? renderDraggableCell() : renderNonDraggableCell();
};

DisplayGridHeaderCell.propTypes = {
  column: DisplayGridColumnPropType.isRequired,
  elementId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isDraggable: PropTypes.bool,
  onColumnResize: PropTypes.func.isRequired,
  onColumnSortChange: PropTypes.func.isRequired,
  onResizeComplete: PropTypes.func.isRequired,
  isActiveGroupBy: PropTypes.bool,
};
